import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PulseIcon, MagnifyingGlassIcon, SpinnerGapIcon, SparkleIcon } from "@phosphor-icons/react";
import { fetchMtfRadar, lookupMtf } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { useSeo } from "../lib/useSeo";

const TONE = { good: "#00E599", warn: "#F5A623", bad: "#FF3366", neutral: "#8A8F98" };
const short = (a) => (a && a.length > 8 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");
const pc = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}${v}%`);
const pcColor = (v) => (v > 0.5 ? "#00E599" : v < -0.5 ? "#FF3366" : "#8A8F98");

const TF = ({ coin }) => (
  <div className="flex items-center gap-1.5 font-mono text-[10px]">
    {[["m5", coin.m5], ["h1", coin.h1], ["h6", coin.h6], ["h24", coin.h24]].map(([k, v]) => (
      <div key={k} className="flex flex-col items-center px-1.5 py-0.5 border border-white/8 bg-black/20 min-w-[40px]">
        <span className="text-[8px] uppercase text-muted-foreground/70">{k}</span>
        <span className="font-bold" style={{ color: pcColor(v) }}>{pc(v)}</span>
      </div>
    ))}
  </div>
);

const Row = ({ c }) => (
  <div data-testid={`mtf-row-${c.mint}`} className="flex items-center gap-3 px-4 py-3 border transition-colors hover:bg-white/[0.03]" style={{ borderColor: `${TONE[c.tone]}22` }}>
    {c.image ? <img src={c.image} alt="" className="w-8 h-8 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
      : <div className="w-8 h-8 border border-white/10 shrink-0" />}
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/coin/${c.mint}`} className="font-heading font-bold hover:text-neon transition-colors">${c.symbol || short(c.mint)}</Link>
        {c.isNew && <span className="font-mono text-[9px] px-1 py-0.5 font-bold" style={{ background: "#7FD4FF18", border: "1px solid #7FD4FF", color: "#7FD4FF" }}>NEW</span>}
        {c.score != null && <span className="font-mono text-[9px] text-muted-foreground">score {c.score}</span>}
        <span className="font-mono text-[9px] text-muted-foreground">· {fmtUsd(c.marketCap)}</span>
      </div>
    </div>
    <TF coin={c} />
  </div>
);

const SECTIONS = [
  { key: "pullback", title: "🟢 Continuation setups", blurb: "Uptrend intact, short-TF fading gently — the low-risk continuation entries you're hunting." },
  { key: "breakout", title: "🚀 Pushing / breaking out", blurb: "Higher-TF up and short-TF accelerating — momentum live right now." },
  { key: "rising", title: "🟢 Rising with trend", blurb: "Trend up, short-TF pushing along with it." },
  { key: "coiling", title: "🟡 Coiling in uptrend", blurb: "Resting flat inside an uptrend — watch for the break." },
  { key: "fading", title: "🟠 Fading at highs", blurb: "Short-TF rolling over while higher-TF still up — exhaustion risk." },
];

export default function MomentumRadar() {
  useSeo({ title: "📊 Momentum Radar — Multi-Timeframe Token States | AlphaApeing",
    description: "Every token classified by m5/h1 vs h6/h24 price action — continuation setups, breakouts, fading & coiling. Search any token to see exactly where it sits." });

  const [q, setQ] = useState("");
  const [lookup, setLookup] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["mtfRadar"], queryFn: fetchMtfRadar, refetchInterval: 45000 });
  const groups = data?.groups || {};

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { setLookup(await lookupMtf(q)); }
    catch { toast.error("Token not found or no live pair"); setLookup(null); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="momentum-radar-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center text-black" style={{ background: "#00E599" }}><PulseIcon size={24} weight="bold" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">MOMENTUM <span style={{ color: "#00E599" }}>RADAR</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm leading-relaxed max-w-2xl">
          Reads each token's <span className="text-white font-semibold">short timeframes (m5/h1)</span> against the <span className="text-white font-semibold">higher-TF figure (h6/h24)</span> — telling you if it's a continuation setup, breaking out, coiling, or fading. Higher-TF sets the bias; short-TF is your timing.
        </p>
      </div>

      <div className="border border-white/10 bg-card p-4">
        <div className="flex gap-2">
          <input data-testid="mtf-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Paste any token address to read its state…" className="flex-1 bg-black/40 border border-white/15 focus:border-neon/60 outline-none text-sm font-mono text-white p-2.5 placeholder:text-muted-foreground/50" />
          <button data-testid="mtf-search-btn" onClick={search} disabled={busy || !q.trim()} className="px-4 py-2 text-sm font-bold text-black disabled:opacity-40" style={{ background: "#00E599" }}>
            {busy ? <SpinnerGapIcon size={16} className="animate-spin" /> : <MagnifyingGlassIcon size={16} weight="bold" />}
          </button>
        </div>
        {lookup && (
          <div className="mt-3 border p-3" style={{ borderColor: `${TONE[lookup.tone]}55`, background: `${TONE[lookup.tone]}0d` }} data-testid="mtf-lookup-result">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={`/coin/${lookup.mint}`} className="font-heading font-black text-lg hover:text-neon">${lookup.symbol || short(lookup.mint)}</Link>
              <span className="font-mono text-sm font-bold" style={{ color: TONE[lookup.tone] }}>{lookup.label}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{lookup.note}</p>
            <div className="mt-2"><TF coin={lookup} /></div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><SpinnerGapIcon size={30} className="animate-spin" style={{ color: "#00E599" }} /></div>
      ) : (
        SECTIONS.map((s) => {
          const rows = groups[s.key] || [];
          if (!rows.length) return null;
          return (
            <div key={s.key} className="space-y-2" data-testid={`mtf-section-${s.key}`}>
              <div>
                <h2 className="font-heading font-black text-lg tracking-tight">{s.title} <span className="font-mono text-xs text-muted-foreground">{rows.length}</span></h2>
                <p className="text-[11px] text-muted-foreground">{s.blurb}</p>
              </div>
              <div className="border border-white/5 divide-y divide-white/5">{rows.map((c) => <Row key={c.mint} c={c} />)}</div>
            </div>
          );
        })
      )}
      {!isLoading && SECTIONS.every((s) => !(groups[s.key] || []).length) && (
        <div className="border border-white/10 bg-card py-14 text-center text-muted-foreground font-mono text-sm px-6 flex flex-col items-center gap-2">
          <SparkleIcon size={26} style={{ color: "#00E599" }} /> No coins in these states right now — search a token above to read it directly.
        </div>
      )}
    </div>
  );
}
